import { md5Hash, base64Decode } from "../utils/crypto";
import { getApiBaseUrl, getProductId } from "../config/env";

// API Configuration
const API_BASE_URL = getApiBaseUrl();

// Cache for basic data from login response
let cachedBasicData = null;

// Get basic data for login (only product_id)
const getLoginBasicData = () => ({
  product_id: getProductId(),
});

// Get full basic data from cache (used for other API calls after login)
const getFullBasicData = () => {
  if (cachedBasicData) {
    return cachedBasicData;
  }
  // Fallback to default basic data if not logged in yet
  return getDefaultBasicData();
};

// Default basic data structure (fallback)
const getDefaultBasicData = () => {
  let userInfo = {};
  try {
    userInfo = JSON.parse(localStorage.getItem("winburst-login-user-info"));
  } catch (error) {
    console.log(error);
  }
  return {
    version_code: userInfo?.version_code || "1",
    media_source: "af_organic",
    dpi: "0",
    imsi: "",
    screen_height: "812",
    country: "US",
    adset: "",
    is_upgrade_user: "0",
    imei: "",
    google_aid: "00000000-0000-0000-0000-000000000000",
    uuid: "EE8D3207-B4A0-4200-AA27-AD5772A998D5",
    os: "2",
    version_name: "1.0.0",
    package_name: "com.word.king.game.ios",
    screen_width: "375",
    android_id: "EE8D3207-B4A0-4200-AA27-AD5772A998D5",
    os_language: "en-US",
    product_id: getProductId(),
    net: "WIFI",
    adgroup: "",
    idfv: "8BD34645-6A8B-4CEA-BCAC-EEEEB577CBF6",
    os_version: "16.3.1",
    phone_model: "iPhone 12 mini",
    install_days: "1",
    campaign: "",
    ab_test: "255",
  };
};

// Cache basic data from login response
export const cacheBasicData = (basicData) => {
  if (basicData) {
    cachedBasicData = basicData;
    // Also save to localStorage for persistence
    localStorage.setItem("winburst_basic_data", JSON.stringify(basicData));
  }
};

// Load basic data from localStorage (call on app init)
export const loadCachedBasicData = () => {
  try {
    const savedBasicData = localStorage.getItem("winburst_basic_data");
    if (savedBasicData) {
      cachedBasicData = JSON.parse(savedBasicData);
    }
  } catch (error) {
    console.error("Failed to load cached basic data:", error);
  }
};

// Clear cached basic data (call on logout)
export const clearCachedBasicData = () => {
  cachedBasicData = null;
  localStorage.removeItem("winburst_basic_data");
};

// Get user token from winburst_user in localStorage
export const getCachedUserToken = () => {
  try {
    const encryptedUser = localStorage.getItem("winburst_user");
    if (encryptedUser) {
      const user = JSON.parse(base64Decode(encryptedUser));
      return user?.user_token || null;
    }
  } catch (error) {
    console.error("Failed to get user token:", error);
  }
  return null;
};

// Get cached user info (email, user_id) from localStorage
export const getCachedUserInfo = () => {
  try {
    const encryptedUser = localStorage.getItem("winburst_user");
    if (encryptedUser) {
      const user = JSON.parse(base64Decode(encryptedUser));
      return {
        email: user?.account || null,
        userId: user?.user_id || null,
        userToken: user?.user_token || null,
      };
    }
  } catch (error) {
    console.error("Failed to get user info:", error);
  }
  return { email: null, userId: null, userToken: null };
};

// Handle 4002 error - clear cache
const handleTokenExpired = () => {
  // Clear all cached data
  clearCachedBasicData();
  localStorage.removeItem("winburst_user");
};

// Make API request
async function makeRequest(url, data, skipToken = false) {
  // Automatically add token to all requests (except login)
  if (!skipToken) {
    const token = getCachedUserToken();
    if (token) {
      data = { ...data, token };
    }
  }

  const formData = new FormData();
  formData.append("sign", "Jhdgh_fasgl1_a)1sfs");
  formData.append("request_data", JSON.stringify(data));

  const response = await fetch(url, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  // Check for 4002 error code (token expired / force logout)
  if (result.code === 4002) {
    handleTokenExpired();
    const error = new Error(
      result.message || "Session expired, please login again",
    );
    error.code = 4002;
    throw error;
  }

  // Check for 400 error code (token invalid / need re-login)
  if (result.code === 400) {
    const error = new Error(
      result.message || "Invalid token, please login again",
    );
    error.code = 400;
    throw error;
  }

  return result;
}

// User Login API
export async function userLogin(email, password) {
  const passwordMd5 = md5Hash(password);

  const requestData = {
    basic: getLoginBasicData(),
    email,
    password: passwordMd5,
  };

  // Pass skipToken=true since login doesn't need token
  const response = await makeRequest(
    `${API_BASE_URL}/sp-apis/v1.0/middle/user/login`,
    requestData,
    true,
  );
  if (response.code === 200 && response.data) {
    if (response?.data?.user_info) {
      localStorage.setItem(
        "winburst-login-user-info",
        JSON.stringify(response?.data?.user_info),
      );
    }
    // Cache the basic data from login response
    if (response.data.basic) {
      cacheBasicData(response.data.basic);
    }

    return response.data;
  } else {
    // Always show generic error message for login failures
    throw new Error("Incorrect email or password!");
  }
}

// User Logout API
export async function userLogout(userId) {
  const requestData = {
    basic: getFullBasicData(),
    user_id: userId,
  };

  const response = await makeRequest(
    `${API_BASE_URL}/sp-apis/v1.0/middle/user/logout`,
    requestData,
  );

  if (response.code === 200) {
    return response.data;
  } else {
    throw new Error(response.message || "Logout failed");
  }
}

// Get Wallet Balance API
export async function getWalletBalance(
  userId,
  coin = 0,
  seasonNum = 0,
  level = 0,
  leaderBoardNum = 0,
) {
  const requestData = {
    basic: getFullBasicData(),
    coin,
    user_id: userId,
    season_num: seasonNum,
    level: level,
    leader_board_num: leaderBoardNum,
  };

  const response = await makeRequest(
    `${API_BASE_URL}/sp-apis/v1.0/middle/user/wallet/get`,
    requestData,
  );

  if (response.code === 200 && response.data) {
    return response.data;
  } else {
    throw new Error(response.message || "Failed to get wallet balance");
  }
}

// Get Session Ticket and SDK URL
export async function getSessionTicket(
  email,
  userId,
  gameCurrencyAlias = "SC",
) {
  const requestData = {
    basic: getFullBasicData(),
    email,
    password: "25D55AD283AA400AF464C76D713C07AD", // Default MD5 password
    user_id: userId,
    game_currency_alias: gameCurrencyAlias,
  };

  const response = await makeRequest(
    `${API_BASE_URL}/sp-apis/v1.0/user/middle/get/session_ticket`,
    requestData,
  );

  if (response.code === 200 && response.data) {
    // ==========================================
    // 本地开发测试：切换到本地 SDK 地址
    // ==========================================
    // if (response.data.sdk_url) {
    //     const urlObj = new URL(response.data.sdk_url);
    //     // 保留原来的 query 参数，只替换 origin
    //     const localUrl = `http://localhost:3002${urlObj.pathname}${urlObj.search}`;
    //     response.data.sdk_url = localUrl;
    //     console.log('[Local Dev] SDK URL switched to:', localUrl);
    // }
    // ==========================================
    return response.data;
  } else if (response.code === 400) {
    // Token 无效，需要重新登录
    const error = new Error(
      response.message || "Invalid token, please login again",
    );
    error.code = 400;
    throw error;
  } else {
    throw new Error(response.message || "Failed to get session ticket");
  }
}

// Recharge wallet API
export async function rechargeWallet(userId, coin = 0, rdMoney = 0) {
  const requestData = {
    basic: getFullBasicData(),
    coin,
    rd_money: rdMoney,
    user_id: userId,
  };

  const response = await makeRequest(
    `${API_BASE_URL}/sp-apis/v1.0/middle/user/wallet/recharge`,
    requestData,
  );

  if (response.code === 200) {
    return response.data;
  } else {
    throw new Error(response.message || "Recharge failed");
  }
}
